import { _notNil, _isNil } from '@/littledash';
import { ScoringScaleEditor } from '@/components/UI/SpreadSheet/editors/ScoringScaleEditor';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type { RefObject } from 'react';
import {
  validateObservationTypeTitle,
  validateObservationTypeLabel,
  getBasicObservationTypeErrorMessage,
  getBasicLabelErrorMessage,
  toObservationTypeRows,
  type ObservationTypeRow,
  type ValidationData,
  type ValidationResult,
} from '@/utils/observationTypeValidation';

type ValidatorCallback = (valid: boolean) => void;

interface BuildColumnsParams {
  validationData: ValidationData | null;
  observationTypes: Array<TeamGlossaryV1>;
  hotTableRef: RefObject<HotTableRef | null>;
}

export const buildColumns = ({
  validationData,
  observationTypes,
  hotTableRef,
}: BuildColumnsParams): SpreadsheetSettings['columns'] => {
  const getSpreadsheetData = (): Array<ObservationTypeRow> => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_notNil(hotInstance) && typeof hotInstance.getData === 'function') {
      return toObservationTypeRows(hotInstance.getData());
    }
    return [];
  };

  const validateTitle = (value: string, rowIndex: number): ValidationResult => {
    return validateObservationTypeTitle(value, rowIndex, validationData, observationTypes, getSpreadsheetData());
  };

  const validateLabel = (value: string, rowIndex: number): ValidationResult => {
    return validateObservationTypeLabel(value, rowIndex, validationData, observationTypes, getSpreadsheetData());
  };

  return [
    {
      data: 0,
      value: 'Observation type',
      type: 'text',
      className: 'htMiddle',
      width: 200,
      validator: function (this: { row: number }, value: unknown, callback: ValidatorCallback) {
        if (_isNil(value) || typeof value !== 'string') {
          callback(false);
          return;
        }

        const rowIndex = this.row;
        if (typeof rowIndex !== 'number') {
          callback(true);
          return;
        }

        const validation = validateTitle(value, rowIndex);
        callback(validation.isValid);
      },
      validationErrorMessage: (value: unknown) => {
        return getBasicObservationTypeErrorMessage(value as string, validationData);
      },
      linkedValidationColumns: ['0'],
    },
    {
      data: 1,
      value: 'Label',
      type: 'text',
      className: 'htMiddle',
      width: 120,
      validator: function (this: { row: number }, value: unknown, callback: ValidatorCallback) {
        if (_isNil(value)) {
          callback(true);
          return;
        }

        if (typeof value !== 'string') {
          callback(false);
          return;
        }

        const rowIndex = this.row;
        if (typeof rowIndex !== 'number') {
          callback(true);
          return;
        }

        const validation = validateLabel(value, rowIndex);
        callback(validation.isValid);
      },
      validationErrorMessage: (value: unknown) => {
        return getBasicLabelErrorMessage(value as string, validationData);
      },
      linkedValidationColumns: ['1'],
    },
    {
      data: 2,
      value: 'Scoring scale',
      type: ScoringScaleEditor.type,
      className: 'htMiddle',
      width: 200,
    },
    {
      data: 3,
      value: 'Grading information',
      type: 'text',
      className: 'htMiddle',
      width: 200,
    },
  ];
};

export const buildCellsCallback = (observationTypes: Array<TeamGlossaryV1>, validationData: ValidationData | null) => {
  return (row: number, _col: number, prop: string | number): Record<string, unknown> => {
    const col = typeof prop === 'number' ? prop : _col;
    const cellProperties: Record<string, unknown> = {};

    if (_notNil(observationTypes[row])) {
      const isArchived = _notNil(observationTypes[row].archived_at);
      const isSystem = observationTypes[row].system === true;

      if (isArchived || isSystem) {
        cellProperties.readOnly = true;
        cellProperties.className = 'htMiddle readOnly';
      } else if (_notNil(validationData)) {
        const isInUse = validationData.in_use_team_glossary_api_ids.includes(observationTypes[row].api_id);
        if (col === 0 && isInUse) {
          cellProperties.readOnly = true;
          cellProperties.className = 'htMiddle readOnly';
        }
      }
    }

    return cellProperties;
  };
};

export const buildAfterRendererCallback = (observationTypes: Array<TeamGlossaryV1>) => {
  return (
    td: HTMLTableCellElement,
    row: number,
    _col: number,
    _prop: string | number,
    _value: unknown,
    cellProperties: object
  ): void => {
    const cp = cellProperties as { readOnly?: boolean };

    if (cp.readOnly === true) {
      const observationType = observationTypes[row];
      if (_isNil(observationType)) {
        return;
      }

      const isArchived = _notNil(observationType.archived_at);
      const isSystem = observationType.system === true;

      if (isSystem) {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', 'This is a system observation type and cannot be modified');
        td.setAttribute('data-type', 'info');
      } else if (isArchived) {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', 'This observation type is archived and cannot be modified');
        td.setAttribute('data-type', 'info');
      } else {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', 'This observation type is in use and cannot be modified');
        td.setAttribute('data-type', 'info');
      }
    }
  };
};
